import { _, underi18n } from "../../vendor";

import Biscuit from "./Biscuit";
import { getUrlParameter } from "./Uri";

// Inbooma i18n catalogs
// @ts-ignore
import en_GB from "../../locales/en_GB.json";
// @ts-ignore
import de_DE from "../../locales/de_DE.json";
// @ts-ignore
import fr_FR from "../../locales/fr_FR.json";

/**
 * Dedicated i18n helper to (auto-)set and get languages,
 * language files ("catalog") and language codes
 *
 * @refs #19518
 */

const languageCatalog = {
  // Map of all available language catalog's where
  // sort order == priority so put dialects after
  // their root/main language (e.g. de_DE = de_DE, de_CH = de_CH)
  en_GB,
  de_DE,
  fr_FR,
};

const defaultLanguage = "de_DE";

const Inboomai18n = class {
  private _lang: "de_DE" | "en_GB" | "fr_FR" = defaultLanguage;

  constructor() {
    this.detectLanguage();
    this.updateVendors();
  }

  updateVendors() {
    // @todo @see ./misc_i18n.js
  }

  getAllLanguages() {
    return languageCatalog;
  }

  getActiveLanguageCatalog() {
    return languageCatalog[this._lang];
  }

  getActiveLanguageCode() {
    return this._lang;
  }

  /**
   * Returns first two character of active langauge string (e.g. "de" / "en")
   */
  getActiveLanguageShortcode() {
    return this.getShortcode(this._lang);
  }

  /**
   * Returns first two characters of string (e.g. "de" / "en")
   * @param {string} language
   * @return {string}
   */
  getShortcode(language: string): string {
    return language.substring(0, 2);
  }

  _changeLocaleOfMonth(language: string, value: any) {
    const enNames: string[] = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    let months: string[] = [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember",
    ];
    let newValue: any = value;
    switch (language) {
      case "de_DE":
        months = [
          "Januar",
          "Februar",
          "März",
          "April",
          "Mai",
          "Juni",
          "Juli",
          "August",
          "September",
          "Oktober",
          "November",
          "Dezember",
        ];
        break;
      case "fr_FR":
        months = [
          "Janvier",
          "Février",
          "Mars",
          "Avril",
          "Mai",
          "Juin",
          "Juillet",
          "Août",
          "Septembre",
          "Octobre",
          "Novembre",
          "Décembre",
        ];
        break;
      case "en_GB":
        months = enNames;
        break;
    }
    if (typeof newValue === "number") {
      return months[newValue];
    }
    months.map((month, index) => {
      newValue = newValue.replace(enNames[index], month);
    });
    return newValue;
  }

  _setLanguage(language: "de_DE" | "en_GB" | "fr_FR") {
    let language2 = "";

    // if(typeof language === 'boolean'){
    //     return this._lang;
    // }
    if (typeof languageCatalog[language] !== "undefined") {
      this._lang = language;
    } else {
      if (language.length >= 5) {
        // Remove _XY - prefix (aka "dialect")
        language2 = this.getShortcode(language);
      }

      if (language2.length == 2) {
        let languageByShortCode: string | null = null;

        // Find root lanuage
        _.each(languageCatalog, function (__, name) {
          if (languageByShortCode == null && name.indexOf(language) == 0) {
            languageByShortCode = name;
          }
        });

        if (languageByShortCode == null) {
          this._lang = defaultLanguage;
        } else {
          this._lang = languageByShortCode;
        }
      } else {
        // Invalid input string
        this._lang = defaultLanguage;
      }
    }

    if (Biscuit.get("token") !== "") {
      Biscuit.set("user.lang", this._lang, 365);
    }
    return this._lang;
  }

  /**
   * @method setLanguage
   *
   * Public method to set session language
   *
   * @param {String} language 2- or 5-digits language-code
   * @return {String} 5-digits language-code
   */
  setLanguage(language: "de_DE" | "en_GB" | "fr_FR") {
    return this._setLanguage(language);
  }

  /**
   * @method detectLanguage
   *
   * Public method to auto-detect and set session language
   * Should only be called once per session.
   *
   * @return {String} 5-digits language-code
   */
  detectLanguage() {
    if (Biscuit.get("token") !== "") {
      // User is logged in

      if (Biscuit.get("user.lang") !== "" && !_.isUndefined(Biscuit.get("user.lang"))) {
        // Priority I: User's cookie lang
        return this._setLanguage(Biscuit.get("user.lang"));
      }
    } else {
      // User is not logged in

      const urlLang: string | undefined | true = getUrlParameter("lang");
      // @ts-ignore
      const browserLang: string | undefined = navigator.language || navigator.userLanguage;

      if (typeof urlLang !== "undefined" && urlLang !== true) {
        // Priority I: URL lang
        return this._setLanguage(urlLang as "de_DE" | "en_GB" | "fr_FR");
      }

      if (typeof browserLang !== "undefined" && browserLang != "") {
        // Priority II: Browser lang
        return this._setLanguage(browserLang as "de_DE" | "en_GB" | "fr_FR");
      }
      // @ts-ignore
      if (typeof window.client_lang !== "undefined" && window.client_lang != "") {
        // Priority III: Client lang (e.g. in register.html)
        // @ts-ignore
        return this._setLanguage(window.client_lang);
      }
    }

    // Last priority: Default lang
    return this._setLanguage(defaultLanguage);
  }

  translate(msg_id: any, keywords: any = null, transformers: any = null) {
    return underi18n.MessageFactory(this.getActiveLanguageCatalog())(
      msg_id,
      keywords,
      transformers,
    );
  }

  /**
   * @method getPikadayI18n
   * @return Object Pikaday translations for active language
   */
  getPikadayI18n() {
    switch (this.getActiveLanguageCode()) {
      case "en_GB":
      default:
        return {
          previousMonth: "Previous Month",
          nextMonth: "Next Month",
          months: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
          weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          weekdaysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        };
      case "de_DE":
        return {
          previousMonth: "Vormonat",
          nextMonth: "Nächster Monat",
          months: [
            "Januar",
            "Februar",
            "März",
            "April",
            "Mai",
            "Juni",
            "Juli",
            "August",
            "September",
            "Oktober",
            "November",
            "Dezember",
          ],
          weekdays: [
            "Sonntag",
            "Montag",
            "Dienstag",
            "Mittwoch",
            "Donnerstag",
            "Freitag",
            "Samstag",
          ],
          weekdaysShort: ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."],
        };
      case "fr_FR":
        return {
          previousMonth: "Mois précédent",
          nextMonth: "Mois prochain",
          months: [
            "Janvier",
            "Février",
            "Mars",
            "Avril",
            "Mai",
            "Juin",
            "Juillet",
            "Août",
            "Septembre",
            "Octobre",
            "Novembre",
            "Décembre",
          ],
          weekdays: [
            "dimanche",
            " lundi ",
            " mardi ",
            " mercredi ",
            " jeudi ",
            " vendredi ",
            " samedi ",
          ],
          weekdaysShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        };
    }
  }
};

export default new Inboomai18n();
